"use client"

import { useState, useMemo } from "react"
import { createPortal } from "react-dom"
import {
  X, Search, Trash2, Eye,
  Phone, Mail, UserX, Ban, Archive,
  Calendar, Clock, RotateCcw, AlertCircle
} from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { useEscapeKey } from "@/hooks/useEscapeKey"
import { LeadSourceBadge } from "./lead-source-badge"
import { LEAD_STAGE_META, normalizeLeadStage } from "@/lib/lead-stages"
import { NIVEL_LABELS } from "@/lib/educational-levels"

// Clasificaciones de archivado con colores
const ARCHIVE_REASONS = {
  perdido: { label: "Perdido", icon: UserX, color: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" },
  spam: { label: "Spam", icon: Ban, color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  no_interesado: { label: "No interesado", icon: X, color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  sin_respuesta: { label: "Sin respuesta", icon: Clock, color: "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300" },
  otro: { label: "Otro", icon: X, color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
}

// Categoría de archivado del lead
const getArchiveReason = (lead) => {
  if (lead.calidad_lead === "spam" || lead.estado_lead === "spam") return "spam"
  if (lead.estado_lead === "perdido") return "perdido"
  const notas = String(lead.notas || "").toLowerCase()
  if (notas.includes("spam")) return "spam"
  if (notas.includes("no interesado") || notas.includes("sin interes")) return "no_interesado"
  if (notas.includes("sin respuesta") || notas.includes("no contesta")) return "sin_respuesta"
  return "otro"
}

// Extraer razón textual y notas adicionales
const parseArchiveNotes = (lead) => {
  const raw = String(lead.notas || "").trim()
  if (!raw) return { razon: null, notas: null }
  const razonMatch = raw.match(/^Raz[oó]n:\s*(.+?)(?:\n|$)/i)
  const notasMatch = raw.match(/\nNotas:\s*(.+)/is)
  return {
    razon: razonMatch ? razonMatch[1].trim() : null,
    notas: notasMatch ? notasMatch[1].trim() : (!razonMatch ? raw : null),
  }
}

// Tiempo relativo legible
const getRelativeArchived = (dateStr) => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return "hoy"
  if (diffDays === 1) return "ayer"
  if (diffDays < 30) return `hace ${diffDays}d`
  const months = Math.floor(diffDays / 30)
  return `hace ${months}m`
}

const formatArchiveDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString("es-MX", { day: "numeric", month: "short" })
}

const getInitials = (name) => {
  if (!name) return "?"
  return name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
}

export function ArchivedLeadsModal({ open, onClose, leads = [], onRestore, onDelete, onViewDetails }) {
  const [activeTab, setActiveTab] = useState("archivados") // "archivados" | "inactivos" | "spam"
  const [searchTerm, setSearchTerm] = useState("")
  const [filterLevel, setFilterLevel] = useState("todos")
  const [confirmDelete, setConfirmDelete] = useState(null)

  useEscapeKey(open, onClose)

  // Tres buckets diferenciados:
  //   - spam       → marcado manual o por calidad_lead
  //   - inactivos  → auto-archivado a 7 días sin respuesta (estado_lead = "inactivo")
  //   - archivados → perdido manual + legacy "cierre"
  const { archivedLeads, inactivoLeads, spamLeads } = useMemo(() => {
    const archived = []
    const inactivos = []
    const spam = []
    leads.forEach((lead) => {
      const stage = normalizeLeadStage(lead.estado_lead || "")
      if (getArchiveReason(lead) === "spam" || stage === "spam") {
        spam.push(lead)
      } else if (stage === "inactivo") {
        inactivos.push(lead)
      } else {
        archived.push(lead)
      }
    })
    return { archivedLeads: archived, inactivoLeads: inactivos, spamLeads: spam }
  }, [leads])

  const activeLeads =
    activeTab === "spam" ? spamLeads :
    activeTab === "inactivos" ? inactivoLeads :
    archivedLeads

  // Filtros aplicados a la lista activa
  const filteredLeads = useMemo(() => {
    let result = [...activeLeads]
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((lead) =>
        (lead.nombre_tutor || "").toLowerCase().includes(term) ||
        (lead.telefono || "").includes(term) ||
        (lead.email || "").toLowerCase().includes(term)
      )
    }
    if (filterLevel !== "todos") {
      result = result.filter((lead) =>
        String(lead.nivel_educativo || "").toLowerCase() === filterLevel.toLowerCase()
      )
    }
    return result
  }, [activeLeads, searchTerm, filterLevel])

  // Conteo por nivel dentro del tab activo
  const levelCounts = useMemo(() => {
    const counts = {}
    activeLeads.forEach((lead) => {
      const nivel = String(lead.nivel_educativo || "").toLowerCase()
      if (nivel) counts[nivel] = (counts[nivel] || 0) + 1
    })
    return counts
  }, [activeLeads])

  const handleRestore = (lead) => onRestore?.(lead)

  const handleDelete = (lead) => {
    if (confirmDelete === lead.id) {
      onDelete?.(lead)
      setConfirmDelete(null)
    } else {
      setConfirmDelete(lead.id)
      setTimeout(() => setConfirmDelete(null), 3000)
    }
  }

  if (!open) return null

  const tabAccent = activeTab === "spam" ? "amber" : "blue"

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-3 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-5xl h-[88vh] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-700/80 overflow-hidden">

        {/* Header */}
        <header className="flex items-start justify-between gap-3 px-6 py-5 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-rose-500 via-red-500 to-rose-600 flex items-center justify-center text-white shadow-lg shadow-rose-500/30 mt-0.5">
              <Archive className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Archivados</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {leads.length} prospecto{leads.length !== 1 ? "s" : ""} archivado{leads.length !== 1 ? "s" : ""} — puedes restaurarlos al tablero
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </header>

        {/* Tabs */}
        <div className="px-6 pt-4 shrink-0">
          <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setActiveTab("archivados")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-all -mb-px ${
                activeTab === "archivados"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              <Archive className="h-4 w-4" />
              Perdidos
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md text-[11px] font-bold ${
                  activeTab === "archivados"
                    ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                }`}
              >
                {archivedLeads.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("inactivos")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-all -mb-px ${
                activeTab === "inactivos"
                  ? "border-slate-500 text-slate-700 dark:text-slate-200"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
              title="Auto-archivados después de 7 días sin respuesta"
            >
              <Clock className="h-4 w-4" />
              Inactivos
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md text-[11px] font-bold ${
                  activeTab === "inactivos"
                    ? "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                }`}
              >
                {inactivoLeads.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("spam")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-all -mb-px ${
                activeTab === "spam"
                  ? "border-amber-500 text-amber-600 dark:text-amber-400"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
            >
              <AlertCircle className="h-4 w-4" />
              Spam
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md text-[11px] font-bold ${
                  activeTab === "spam"
                    ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
                }`}
              >
                {spamLeads.length}
              </span>
            </button>
          </div>
        </div>

        {/* Search + filtro nivel */}
        <div className="px-6 py-3 shrink-0 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o teléfono..."
              className="pl-10 h-9 text-sm"
            />
          </div>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="h-9 px-3 rounded-md text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-pointer text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
          >
            <option value="todos">Todos los niveles</option>
            {Object.entries(NIVEL_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label} {levelCounts[key] ? `(${levelCounts[key]})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Tabla */}
        <div className="flex-1 overflow-y-auto px-2">
          {filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                {activeTab === "spam" ? (
                  <AlertCircle className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                ) : (
                  <Archive className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                )}
              </div>
              <p className="text-base font-semibold text-slate-600 dark:text-slate-400">
                {activeLeads.length === 0
                  ? activeTab === "spam"
                    ? "Sin leads marcados como spam"
                    : activeTab === "inactivos"
                      ? "Sin leads inactivos"
                      : "Sin leads perdidos"
                  : "Sin resultados"}
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                {activeLeads.length === 0
                  ? activeTab === "inactivos"
                    ? "Aparecen aquí tras 7 días sin respuesta"
                    : "Aparecerán aquí cuando se archiven"
                  : "Intenta con otro término de búsqueda"}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-[10px] uppercase font-semibold text-slate-400 tracking-wider">
                  <th className="text-left px-4 py-2.5">Prospecto</th>
                  <th className="text-left px-2 py-2.5">Última etapa</th>
                  <th className="text-left px-2 py-2.5">Origen</th>
                  <th className="text-left px-2 py-2.5">Archivado</th>
                  <th className="px-4 py-2.5 w-[180px]"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => {
                  const archiveInfo = parseArchiveNotes(lead)
                  const stage = normalizeLeadStage(lead.estado_lead)
                  const stageTitle = LEAD_STAGE_META[stage]?.title || lead.estado_lead || "—"
                  const stageAccent = LEAD_STAGE_META[stage]?.accent || "#94a3b8"
                  const archiveDate = lead.fecha_archivado || lead.updated_at

                  return (
                    <tr
                      key={lead.id}
                      className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group"
                    >
                      {/* Prospecto */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center text-slate-700 dark:text-slate-200 font-bold text-sm shrink-0">
                            {getInitials(lead.nombre_tutor)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-900 dark:text-white truncate">
                              {lead.nombre_tutor || "Sin nombre"}
                            </p>
                            {lead.telefono && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                                {lead.telefono}
                              </p>
                            )}
                            {(archiveInfo.razon || archiveInfo.notas) && (
                              <p className="text-[11px] italic text-slate-400 dark:text-slate-500 truncate max-w-[280px] mt-0.5">
                                {archiveInfo.razon || archiveInfo.notas}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Última etapa con punto de color */}
                      <td className="px-2 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
                          <span
                            className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stageAccent }}
                          />
                          {stageTitle}
                        </span>
                      </td>

                      {/* Origen */}
                      <td className="px-2 py-3">
                        {lead.source ? (
                          <LeadSourceBadge
                            source={lead.source}
                            sourceDetail={lead.source_detail}
                            expanded
                          />
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>

                      {/* Archivado: hace Xd + fecha */}
                      <td className="px-2 py-3">
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          <p className="font-medium">{getRelativeArchived(archiveDate) || "—"}</p>
                          {archiveDate && (
                            <p className="text-[10px] text-slate-400 dark:text-slate-500">
                              {formatArchiveDate(archiveDate)}
                            </p>
                          )}
                        </div>
                      </td>

                      {/* Acciones — siempre visibles, estilo discreto */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => onViewDetails?.(lead)}
                            className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRestore(lead)}
                            className="h-8 px-2.5 flex items-center gap-1 rounded-md bg-emerald-50 hover:bg-emerald-500 dark:bg-emerald-950/40 dark:hover:bg-emerald-500 text-emerald-700 hover:text-white dark:text-emerald-300 dark:hover:text-white text-xs font-bold border border-emerald-200 hover:border-emerald-500 dark:border-emerald-800/50 transition-colors"
                            title="Restaurar al tablero"
                          >
                            <RotateCcw className="h-3 w-3" />
                            <span>Restaurar</span>
                          </button>
                          <button
                            onClick={() => handleDelete(lead)}
                            className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${
                              confirmDelete === lead.id
                                ? "bg-red-500 text-white animate-pulse"
                                : "text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500"
                            }`}
                            title={confirmDelete === lead.id ? "Clic de nuevo para eliminar" : "Eliminar permanentemente"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <footer className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 shrink-0 flex items-center justify-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded bg-slate-200 dark:bg-slate-700 font-bold">
            {filteredLeads.length}
          </span>
          <span>de {activeLeads.length} {activeTab === "spam" ? "spam" : activeTab === "inactivos" ? "inactivos" : "perdidos"}</span>
          <span className="text-slate-300 dark:text-slate-600">•</span>
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">Restaurar para mover al tablero</span>
        </footer>
      </div>
    </div>
  )

  return typeof document !== "undefined" ? createPortal(modalContent, document.body) : null
}

export default ArchivedLeadsModal
